import {MenuItem, styled, Typography, useTheme} from "@mui/material";
import {StyledSelect as Select} from "components/Input/styled";
import PlusIcon from "assets/icons/PlusIcon";
import {memo, useState} from "react";
import shortid from "shortid";
import CloseIcon from "assets/icons/CloseIcon";
import {Field, useFormikContext} from "formik";
import factorDetailsItems from "assets/js/factorDetailsItems";
import {invoiceInitialValues} from "store/reducers/factorReducer";

const FactorDetailsSettingContainer = styled("div")(({theme}) => ({
    padding: "16px",
}));

const CustomizationContainer = styled("div")(({theme}) => ({
    padding: "12px",
    position: "relative",
    justifyContent: "space-between",
    border: "1px solid #F1F1F1",
}));
const StyledSelect = styled(Select)(({theme}) => ({
    fontSize: 14,
    width: "100%",
    "&.MuiOutlinedInput-root": {
        height: "80px",
    },
}));

const backgroundImage =
    "data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' stroke='%23DBDEE0FF' strokeWidth='4' stroke-dasharray='14%2c 10' stroke-dashoffset='8' stroke-linecap='square'/%3e%3c/svg%3e";

const ButtonContainer = styled("div")(({theme}) => ({
    width: "16%",
    position: "relative",
    height: "80px",
    borderRadius: "4px",
    backgroundImage: `url("${backgroundImage}")`,
    backgroundSize: "100% 100%",
    backgroundColor: "unset",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
}));
const InnerButton = styled("div")(({theme}) => ({
    width: "80%",
    height: "70px",
    borderRadius: "4px",
    backgroundColor: "#F5F5F5",
    flexDirection: "column",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
}));

const AddButton = styled("div")(({theme}) => ({
    width: "32px",
    height: "32px",
    backgroundColor: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "50%",
    cursor: "pointer",
}));

const AddRowButton = styled(AddButton)(({theme}) => ({
    position: "absolute",
    bottom: "-16px",
    left: 0,
    right: 0,
    margin: "auto",
}));

const RowContainer = styled("div")(({theme}) => ({
    display: "flex",
    marginBottom: "5px",
    justifyContent: "space-between",
    // backgroundColor: 'red',
}));
const SelectContainer = styled("div")(({theme, items}: { theme: ThemeObjType; items?: number }) => {
    let initialWidth = 96;
    // @ts-ignore
    if (items > 0) initialWidth = Math.floor(96 / items);
    return {
        display: "grid",
        gridTemplateColumns: `repeat(auto-fill, ${initialWidth}%)`,
        justifyContent: "space-evenly",
        width: "80%",
    };
});

const SelectComponentContainer = styled("div")(({theme}) => ({
    position: "relative",
}));
const CloseContainer = styled("div")(({theme}) => ({
    position: "absolute",
    width: "15px",
    height: "15px",
    borderRadius: "50%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#DCDCDC",
    left: "-5px",
    top: "-5px",
    // zIndex: 2,
    cursor: "pointer",
}));

const SelectComponent = memo(
    ({
         removeColumn,
         updateValue,
         value,
     }: {
        removeColumn: () => void;
        updateValue: (value: string) => void;
        value: string;
    }) => {
        const [showClose, setShowClose] = useState(false);
        return (
            <SelectComponentContainer
                onMouseEnter={() => setShowClose(true)}
                onMouseLeave={() => setShowClose(false)}
            >
                <Field
                    name="factorDetails"
                    as={StyledSelect}
                    placholder="کدام بخش از فاکتور در این قسمت نمایش داده شود؟"
                    // @ts-ignore
                    onChange={(event) => {
                        updateValue(event.target.value);
                    }}
                    value={value}
                >
                    {factorDetailsItems.map((item) => (
                        <MenuItem
                            style={{justifyContent: "end", fontSize: 12}}
                            key={shortid.generate()}
                            value={item.value}
                        >
                            {item.title}
                        </MenuItem>
                    ))}
                </Field>
                {showClose && (
                    <CloseContainer onClick={() => removeColumn()}>
                        <CloseIcon scale={0.3}/>
                    </CloseContainer>
                )}
            </SelectComponentContainer>
        );
    }
);

const RowComponent = memo(
    ({
         updateValue,
         addColumn,
         rowTitle,
         columns,
         removeColumn,
     }: {
        addColumn: () => void;
        columns: any[];
        rowTitle: string;
        removeColumn: (rowTitle: string, columnTitle: string) => void;
        updateValue: (rowTitle: string, columnTitle: string, value: string) => void;
    }) => {
        return (
            <RowContainer>
                {/* @ts-ignore */}
                <SelectContainer items={columns.length}>
                    {columns.map((item, key) => (
                        <SelectComponent
                            value={item.value}
                            removeColumn={() => removeColumn(rowTitle, item.title)}
                            updateValue={(value: string) => {
                                updateValue(rowTitle, item.title, value);
                            }}
                            key={shortid.generate()}
                        />
                    ))}
                </SelectContainer>
                <ButtonContainer onClick={() => addColumn()}>
                    <AddContentButton/>
                </ButtonContainer>
            </RowContainer>
        );
    }
);

const AddContentButton = () => {
    return (
        <InnerButton>
            <AddButton>
                <PlusIcon scale={0.7}/>
            </AddButton>
        </InnerButton>
    );
};

const countColumns = (rows: any[]) => {
    let count = 0;

    rows.forEach((item) => {
        count += item.columns.length;
    });

    return count;
};

const FactorDetailsSetting = () => {
    const formikContext = useFormikContext();

    const {values} = formikContext as { values: typeof invoiceInitialValues };
    const rows = values.invoiceDetails;

    const setRows = (value: any) => {
        formikContext.setFieldValue("invoiceDetails", value);
    };
    const theme = useTheme();

    const addRow = () => {
        if (rows.length < 3) {
            const newRows = [
                ...rows,
                {
                    title: shortid.generate(),
                    columns: [{title: shortid.generate(), value: null}],
                },
            ];
            setRows(newRows);
        }
    };
    const addColumn = (rowTitle: string) => {
        const index = rows.findIndex((object) => object.title === rowTitle);
        const row = rows[index];
        if (row.columns.length < 3) {
            const columns = row.columns;
            const newRows = [...rows];
            newRows[index] = {
                title: row.title,
                columns: [
                    // @ts-ignore
                    ...columns,
                    {title: shortid.generate(), value: "", order: columns.length + 1},
                ],
            };
            setRows(newRows);
        }
    };
    const removeColumn = (rowTitle: string, columnTitle: string) => {
        const rowIndex = rows.findIndex((object) => object.title === rowTitle);
        const row = rows[rowIndex];
        const columns = row.columns;
        // @ts-ignore
        const columnIndex = columns.findIndex((object) => object.title === columnTitle);
        columns.splice(columnIndex, 1);
        const newRows = [...rows];
        newRows[rowIndex] = {
            title: row.title,
            columns: columns,
        };
        if (columns.length === 0) {
            newRows.splice(rowIndex, 1);
        }
        setRows(newRows);
    };
    const updateValue = (rowTitle: string, columnTitle: string, value: string) => {
        const rowIndex = rows.findIndex((object) => object.title === rowTitle);
        const row = rows[rowIndex];
        const columns = row.columns;
        // @ts-ignore
        const columnIndex = columns.findIndex((object) => object.title === columnTitle);
        const column = columns[columnIndex];
        // @ts-ignore
        columns[columnIndex] = {title: column.title, value: value, order: column.order};
        const newRows = [...rows];
        newRows[rowIndex] = {
            title: row.title,
            columns: columns,
        };
        setRows(newRows);
    };
    return (
        <FactorDetailsSettingContainer>
            <Typography
                sx={{paddingBottom: 16, paddingTop: 16}}
                color={theme.palette.disabled.main}
                variant="caption"
            >
                در این قسمت می توانید ترتیب نمایش اطلاعات مختلف فاکتور را تغییر دهید.
            </Typography>
            <CustomizationContainer>
                {rows.map((item, key) => (
                    <RowComponent
                        rowTitle={item.title}
                        columns={item.columns}
                        addColumn={() => addColumn(item.title)}
                        key={key}
                        removeColumn={removeColumn}
                        updateValue={updateValue}
                    />
                ))}

                {countColumns(rows) < 10 && (
                    <AddRowButton onClick={addRow}>
                        <PlusIcon scale={0.7}/>
                    </AddRowButton>
                )}
            </CustomizationContainer>
        </FactorDetailsSettingContainer>
    );
};

export default FactorDetailsSetting;
